import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Archive } from 'lucide-react';
import { Badge, Button, Card, CardContent } from '@/components/ui';
import type { IqServiceInventoryItem } from '@/types/iq-service-inventory';
import { IQ_SERVICE_STATUS_ORDER, iqServiceStatusBadgeVariant } from './iq-service-inventory-status';
import { IqServiceUsageBadge } from './iq-service-inventory-usage-badge';

interface IqServiceInventoryListProps {
  items: IqServiceInventoryItem[];
  onSelect: (item: IqServiceInventoryItem) => void;
  /** Optional write actions (UC-3/4). Omitted = read-only list. */
  onEdit?: (item: IqServiceInventoryItem) => void;
  onArchive?: (item: IqServiceInventoryItem) => void;
}

/** Inventory rows grouped by status (UC-1). Row opens the detail; optional edit/archive actions. */
export function IqServiceInventoryList({ items, onSelect, onEdit, onArchive }: IqServiceInventoryListProps) {
  const { t } = useTranslation('iq-service-inventory');

  const groups = useMemo(
    () =>
      IQ_SERVICE_STATUS_ORDER.map((status) => ({
        status,
        items: items.filter((item) => item.status === status),
      })).filter((group) => group.items.length > 0),
    [items],
  );

  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <section key={group.status} aria-label={t(`status.${group.status}`)}>
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
            <Badge variant={iqServiceStatusBadgeVariant(group.status)}>
              {t(`status.${group.status}`)}
            </Badge>
            <span className="text-muted-foreground">({group.items.length})</span>
          </h3>

          <div className="space-y-2">
            {group.items.map((item) => (
              <Card key={item.iqServiceKey}>
                <CardContent className="flex flex-wrap items-center justify-between gap-3 p-3">
                  <button
                    type="button"
                    onClick={() => onSelect(item)}
                    className="min-w-0 flex-1 rounded text-left hover:opacity-80"
                    aria-label={t('list.viewDetail')}
                  >
                    <div className="truncate font-mono text-sm">{item.iqServiceKey}</div>
                    {item.displayName && (
                      <div className="truncate text-xs text-muted-foreground">{item.displayName}</div>
                    )}
                  </button>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    {item.flowType && (
                      <Badge variant="outline">{t(`flowType.${item.flowType}`)}</Badge>
                    )}
                    {item.bookingType && (
                      <Badge variant="outline">{t(`bookingType.${item.bookingType}`)}</Badge>
                    )}
                    {/* Spec 347 §6: BFF-derived usage (Dimension a) — entry count, not usedBy.length */}
                    <IqServiceUsageBadge item={item} />
                    {onEdit && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t('list.edit')}
                        onClick={() => onEdit(item)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {onArchive && item.status !== 'archived' && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t('list.archive')}
                        onClick={() => onArchive(item)}
                      >
                        <Archive className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
